"use client";

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';


type DietaryBadge = 'GF' | 'V' | 'VG' | 'DF';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image?: string;
  alt?: string;
  dietary: DietaryBadge[];
  category: string;
  isNew?: boolean;
  isPopular?: boolean;
}

export default function MenuCategories() {
  const [activeFilter, setActiveFilter] = useState<DietaryBadge | 'All'>('All');

  const menuItems: MenuItem[] = [
  // Appetizers
  {
    id: 'app_bruschetta',
    name: 'Truffle Bruschetta',
    description: 'Toasted ciabatta with black truffle, ricotta, and honey drizzle',
    price: 14,
    image: "https://images.unsplash.com/photo-1638525136572-f667b0d9e7ee",
    alt: 'Toasted bruschetta topped with creamy ricotta, black truffle shavings, and honey on rustic plate',
    dietary: ['V'],
    category: 'Appetizers',
    isNew: true
  },
  {
    id: 'app_calamari',
    name: 'Crispy Calamari',
    description: 'Lightly fried squid with lemon aioli and marinara',
    price: 16,
    dietary: [],
    category: 'Appetizers',
    isPopular: true
  },
  {
    id: 'app_spring_rolls',
    name: 'Vietnamese Spring Rolls',
    description: 'Fresh rice paper rolls with shrimp, herbs, and peanut sauce',
    price: 12,
    dietary: ['GF', 'DF'],
    category: 'Appetizers'
  },
  {
    id: 'app_hummus',
    name: 'Mediterranean Hummus',
    description: 'Creamy chickpea dip with olive oil, paprika, and warm pita',
    price: 11,
    dietary: ['V', 'VG', 'DF'],
    category: 'Appetizers'
  },

  // Main Courses
  {
    id: 'main_pasta',
    name: 'Signature Truffle Pasta',
    description: 'Handmade fettuccine with black truffle, wild mushrooms, and parmesan',
    price: 32,
    image: "https://images.unsplash.com/photo-1533109337296-4e5a3107a6eb",
    alt: 'Handmade fettuccine pasta with black truffle shavings and wild mushrooms on white plate',
    dietary: ['V'],
    category: 'Main Courses',
    isPopular: true
  },
  {
    id: 'main_salmon',
    name: 'Pan-Seared Atlantic Salmon',
    description: 'Wild-caught salmon with lemon butter, asparagus, and roasted potatoes',
    price: 38,
    image: "https://images.unsplash.com/photo-1734113436466-135fd98b0abf",
    alt: 'Pan-seared salmon fillet with crispy skin, served with asparagus and roasted potatoes',
    dietary: ['GF', 'DF'],
    category: 'Main Courses'
  },
  {
    id: 'main_wagyu',
    name: 'Wagyu Ribeye Steak',
    description: 'Premium A5 wagyu with roasted vegetables and red wine reduction',
    price: 85,
    dietary: ['GF', 'DF'],
    category: 'Main Courses'
  },
  {
    id: 'main_risotto',
    name: 'Mushroom Risotto',
    description: 'Creamy arborio rice with porcini, shiitake, and truffle oil',
    price: 28,
    dietary: ['V', 'GF'],
    category: 'Main Courses',
    isNew: true
  },
  {
    id: 'main_ramen',
    name: 'Tonkotsu Ramen',
    description: 'Rich pork bone broth with chashu, soft egg, and bamboo shoots',
    price: 22,
    dietary: ['DF'],
    category: 'Main Courses',
    isPopular: true
  },
  {
    id: 'main_curry',
    name: 'Thai Green Curry',
    description: 'Coconut curry with vegetables, tofu, and jasmine rice',
    price: 24,
    dietary: ['V', 'VG', 'GF', 'DF'],
    category: 'Main Courses'
  },

  // Desserts
  {
    id: 'dessert_tiramisu',
    name: 'Classic Tiramisu',
    description: 'Espresso-soaked ladyfingers with mascarpone and cocoa',
    price: 12,
    dietary: ['V'],
    category: 'Desserts',
    isPopular: true
  },
  {
    id: 'dessert_cheesecake',
    name: 'New York Cheesecake',
    description: 'Creamy cheesecake with berry compote and graham cracker crust',
    price: 11,
    dietary: ['V'],
    category: 'Desserts'
  },
  {
    id: 'dessert_chocolate',
    name: 'Molten Chocolate Lava Cake',
    description: 'Warm chocolate cake with liquid center, vanilla ice cream',
    price: 13,
    dietary: ['V'],
    category: 'Desserts',
    isNew: true
  },
  {
    id: 'dessert_panna',
    name: 'Vanilla Panna Cotta',
    description: 'Silky Italian custard with fresh berries and mint',
    price: 10,
    dietary: ['V', 'GF'],
    category: 'Desserts'
  },

  // Beverages
  {
    id: 'bev_wine_red',
    name: 'Cabernet Sauvignon',
    description: 'Full-bodied red wine from Napa Valley',
    price: 14,
    dietary: ['V', 'VG', 'GF', 'DF'],
    category: 'Beverages'
  },
  {
    id: 'bev_wine_white',
    name: 'Chardonnay',
    description: 'Crisp white wine with notes of apple and citrus',
    price: 12,
    dietary: ['V', 'VG', 'GF', 'DF'],
    category: 'Beverages'
  },
  {
    id: 'bev_cocktail',
    name: 'Signature Old Fashioned',
    description: 'Bourbon, bitters, orange peel, and cherry',
    price: 16,
    dietary: ['V', 'VG', 'GF', 'DF'],
    category: 'Beverages',
    isPopular: true
  },
  {
    id: 'bev_mocktail',
    name: 'Berry Mint Spritzer',
    description: 'Fresh berries, mint, sparkling water, and lime',
    price: 8,
    dietary: ['V', 'VG', 'GF', 'DF'],
    category: 'Beverages'
  }];


  const dietaryFilters: {id: string;label: DietaryBadge | 'All';description: string;}[] = [
  { id: 'filter_all', label: 'All', description: 'All Items' },
  { id: 'filter_gf', label: 'GF', description: 'Gluten-Free' },
  { id: 'filter_v', label: 'V', description: 'Vegetarian' },
  { id: 'filter_vg', label: 'VG', description: 'Vegan' },
  { id: 'filter_df', label: 'DF', description: 'Dairy-Free' }];


  const categories = ['Appetizers', 'Main Courses', 'Desserts', 'Beverages'];

  const filteredItems =
  activeFilter === 'All' ?
  menuItems :
  menuItems.filter((item) => item.dietary.includes(activeFilter));

  return (
    <section className="py-16 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Dietary Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-16 animate-fade-in">
          <span className="text-sm font-medium text-muted-foreground mr-2">Filter by:</span>
          {dietaryFilters.map((filter) =>
          <button
            key={filter.id}
            onClick={() => setActiveFilter(filter.label)}
            className={`px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider transition-all ${
            activeFilter === filter.label ?
            'bg-primary text-primary-foreground shadow-lg' :
            'bg-card border border-border text-muted-foreground hover:border-primary hover:text-primary'}`
            }>

              {filter.description}
            </button>
          )}
        </div>

        {/* Menu Categories */}
        {categories.map((category) => {
          const categoryItems = filteredItems.filter((item) => item.category === category);

          if (categoryItems.length === 0) return null;

          return (
            <div key={`category_${category}`} className="mb-20 animate-slide-up" id={category.toLowerCase().replace(' ', '-')}>
              <h3 className="font-serif text-3xl md:text-4xl text-foreground mb-10 pb-4 border-b border-border/50">
                {category}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {categoryItems.map((item) =>
                <div
                  key={item.id}
                  className="group bg-card rounded-2xl p-6 border border-border/50 hover:shadow-xl transition-all duration-500 hover:-translate-y-1">

                    <div className="flex gap-6">
                      {/* Image (if available) */}
                      {item.image &&
                    <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0">
                          <AppImage
                        src={item.image}
                        alt={item.alt || item.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />

                        </div>
                    }

                      {/* Content */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h4 className="font-serif text-xl font-semibold text-foreground mb-2">
                              {item.name}
                            </h4>
                            {/* Badges */}
                            <div className="flex flex-wrap gap-2 mb-2">
                              {item.isNew &&
                            <span className="px-2 py-1 rounded-full bg-accent/10 text-accent text-xs font-bold uppercase tracking-wider">
                                  New
                                </span>
                            }
                              {item.isPopular &&
                            <span className="px-2 py-1 rounded-full bg-warning/10 text-warning text-xs font-bold uppercase tracking-wider">
                                  Popular
                                </span>
                            }
                              {item.dietary.map((badge) =>
                            <span
                              key={`${item.id}_${badge}`}
                              className="px-2 py-1 rounded-full bg-success/10 text-success text-xs font-bold uppercase tracking-wider">

                                  {badge}
                                </span>
                            )}
                            </div>
                          </div>
                          <span className="text-2xl font-serif font-bold text-primary shrink-0">
                            ${item.price}
                          </span>
                        </div>

                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>);

        })}
      </div>
    </section>);

}